// User-facing (Spanish) labels for every enum value. This is the only place
// where enum codes are translated for display; the codes themselves are English.
import { UMA_CASH_LIMIT } from "./constants";
import type { DeadlineStatus } from "./deadlines";
import { formatMoney, formatNumber } from "./money";
import type { NoticeKind } from "./notices";
import type { AutomaticRiskReason } from "./risk";
import type { DeactivationBlock } from "./team";
import { type Thresholds, withVat } from "./thresholds";
import type {
  ClientType,
  DocumentType,
  ExtractionSource,
  IdDocumentType,
  PaymentMethod,
  RiskLevel,
  Role,
} from "./types";

export const ROLE_LABELS: Record<Role, string> = {
  chamber_admin: "Administrador de la Cámara",
  company_admin: "Responsable de Cumplimiento",
  operator: "Operador de mostrador",
};

export const CLIENT_TYPE_LABELS: Record<ClientType, string> = {
  individual: "Persona física",
  foreign_individual: "Persona física extranjera",
  legal_entity: "Persona moral",
  foreign_legal_entity: "Persona moral extranjera",
  trust: "Fideicomiso",
};

export const RISK_LEVEL_LABELS: Record<RiskLevel, string> = {
  low: "Bajo",
  medium: "Medio",
  high: "Alto",
};

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  transfer: "Transferencia",
  card: "Tarjeta",
  check: "Cheque nominativo",
  cash: "Efectivo",
};

export const ID_DOCUMENT_TYPE_LABELS: Record<IdDocumentType, string> = {
  ine: "Credencial para votar (INE)",
  passport: "Pasaporte",
  professional_license: "Cédula profesional",
  residence_card: "Tarjeta de residente",
  military_card: "Cartilla del Servicio Militar",
  other: "Otra",
};

export const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  id_document: "Identificación oficial (INE o pasaporte)",
  curp: "CURP",
  tax_certificate: "Constancia de situación fiscal",
  proof_of_address: "Comprobante de domicilio",
  articles_of_incorporation: "Acta constitutiva",
  power_of_attorney: "Poder notarial",
  representative_id: "Identificación del representante legal",
  beneficial_owner_declaration: "Declaración de Beneficiario Controlador",
  beneficial_owner_id: "Identificación del Beneficiario Controlador",
  immigration_document: "Documento migratorio",
  trust_agreement: "Contrato de fideicomiso",
  trust_parties: "Datos de las partes del fideicomiso",
  other: "Otro documento",
};

/** Where automatically filled data came from ("Datos tomados de …"). */
export const EXTRACTION_SOURCE_LABELS: Record<ExtractionSource, string> = {
  tax_certificate: "la Constancia de Situación Fiscal",
  curp_certificate: "la constancia de la CURP",
};

/** Why a cash operation is refused (art. 32): shown in the form and returned by the API. */
export function cashLimitExceededMessage(amount: number, thresholds: Pick<Thresholds, "cashLimit">): string {
  return (
    `Excede el límite de efectivo: ${formatMoney(withVat(amount))} con IVA alcanza o rebasa el tope de ` +
    `${formatMoney(thresholds.cashLimit)} (${formatNumber(UMA_CASH_LIMIT)} UMA, art. 32 LFPIORPI), ` +
    "así que no puede pagarse en efectivo. Cambia la forma de pago o revisa el límite en Configuración."
  );
}

export const DEADLINE_STATUS_LABELS: Record<DeadlineStatus | "not_required", string> = {
  filed: "Presentado",
  overdue: "Vencido",
  due_soon: "Por vencer",
  on_time: "En plazo",
  not_required: "No requerido",
};

export const NOTICE_KIND_LABELS: Record<NoticeKind, string> = {
  individual: "Operación individual",
  accumulated: "Acumulación de 6 meses",
};

export const RISK_REASON_LABELS: Record<AutomaticRiskReason, string> = {
  foreign_pep: "Persona Políticamente Expuesta extranjera",
  high_risk_jurisdiction: "Residente en una jurisdicción de alto riesgo (GAFI)",
};

export const DEACTIVATION_BLOCK_LABELS: Record<DeactivationBlock, string> = {
  self: "No puedes desactivar tu propio acceso.",
  last_company_admin: "La empresa debe conservar al menos un Responsable de Cumplimiento activo.",
};

/** Shown to a deactivated member who tries to sign in (or still has the portal open). */
export const ACCOUNT_DEACTIVATED_MESSAGE =
  "Tu acceso al portal fue desactivado. Si crees que es un error, comunícate con el Responsable de Cumplimiento de tu empresa o con la Cámara.";
